import { Manifest, StateFile } from '@/src/io/state-file/schema';
import { Maybe } from '@/src/types';
import type { AnnotationToolStore } from '@/src/store/tools/useAnnotationTool';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useCropStore } from './crop';
import { useCrosshairsToolStore } from './crosshairs';
import { usePaintToolStore } from './paint';
import { useRulerStore } from './rulers';
import { useRectangleStore } from './rectangles';
import { AnnotationToolType, IToolStore, Tools } from './types';
import { usePolygonStore } from './polygons';
import { useToolSelectionStore } from './toolSelection';
import { useViewStore } from '@/src/store/views';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { getEnFaceUnavailableReason } from '@/src/enface/availability';
import {
  EffectiveView,
  getEffectiveView,
} from '@/src/core/views/effectiveView';

const CINE_DISALLOWED_TOOLS = new Set([
  Tools.Paint,
  Tools.Crop,
  Tools.Crosshairs,
]);

const EN_FACE_TOOLS = new Set([
  Tools.Select,
  Tools.WindowLevel,
  Tools.Pan,
  Tools.Zoom,
  Tools.Crosshairs,
]);

export function isToolAllowedFor(tool: Tools, effective: EffectiveView | null) {
  if (effective?.viewInfo.type === 'EnFace') return EN_FACE_TOOLS.has(tool);
  if (effective?.kind === 'cine' && CINE_DISALLOWED_TOOLS.has(tool))
    return false;
  return true;
}

export function getToolUnavailableReason(
  tool: Tools,
  effective: EffectiveView | null
) {
  if (!isToolAllowedFor(tool, effective)) {
    return effective?.viewInfo.type === 'EnFace'
      ? 'This tool is unavailable on an En face projection. Switch to a slice view to use it.'
      : 'This tool is unavailable for a cine clip.';
  }
  if (effective?.viewInfo.type !== 'EnFace' || tool === Tools.Select)
    return null;
  return getEnFaceUnavailableReason(effective.renderDataID);
}

const activeEffectiveView = () => getEffectiveView(useViewStore().activeView);

function coerceForEffective(tool: Tools, effective: EffectiveView | null) {
  return isToolAllowedFor(tool, effective) ? tool : Tools.Select;
}

// TODO move these types out
export const AnnotationToolStoreMap: Record<
  AnnotationToolType,
  () => AnnotationToolStore
> = {
  [AnnotationToolType.Polygon]: usePolygonStore,
  [AnnotationToolType.Rectangle]: useRectangleStore,
  [AnnotationToolType.Ruler]: useRulerStore,
} as const;

export const ToolStoreMap: Record<Tools, Maybe<() => IToolStore>> = {
  [Tools.Pan]: null,
  [Tools.WindowLevel]: null,
  [Tools.Zoom]: null,
  [Tools.Select]: null,
  [Tools.Crop]: useCropStore,
  [Tools.Crosshairs]: useCrosshairsToolStore,
  [Tools.Paint]: usePaintToolStore,
  ...AnnotationToolStoreMap,
} as const;

export function useAnnotationToolStore(
  type: AnnotationToolType
): AnnotationToolStore {
  const useStore = AnnotationToolStoreMap[type];
  return useStore();
}

/**
 * Deletes every currently selected annotation, across all annotation types.
 *
 * removeTool() drops the tool from the selection as it goes, so iterate over a
 * snapshot rather than the live selection.
 */
export function removeSelectedTools() {
  const selectionStore = useToolSelectionStore();

  [...selectionStore.selection].forEach(({ id, type }) => {
    useAnnotationToolStore(type).removeTool(id);
  });

  // clears any entry whose tool was already gone
  selectionStore.clearSelection();
}

/**
 * Returns true if the tool is ready to be
 * activated. By default, a tool without a
 * store setup() will be activated.
 */
function setupTool(tool: Tools) {
  const useStore = ToolStoreMap[tool];
  const store = useStore?.();
  if (store?.activateTool) {
    return store.activateTool?.();
  }
  return true;
}

function teardownTool(tool: Tools) {
  const useStore = ToolStoreMap[tool];
  const store = useStore?.();
  if (store) {
    store.deactivateTool?.();
  }
}

export const useToolStore = defineStore('tool', () => {
  const currentTool = ref(Tools.WindowLevel);
  const toolBeforeTemporaryCrosshairs = ref<Tools>();
  const { currentImageID } = useCurrentImage('global');

  function setCurrentTool(tool: Tools) {
    const effective = activeEffectiveView();
    const coerced = coerceForEffective(tool, effective);
    if (coerced === tool && getToolUnavailableReason(tool, effective)) return;
    applyToolSelection(coerced);
  }

  function applyToolSelection(coerced: Tools) {
    if (currentTool.value === coerced) {
      return;
    }
    if (!setupTool(coerced)) {
      return;
    }
    teardownTool(currentTool.value);
    currentTool.value = coerced;
  }

  // Shared by the toolbar and the Paint hints, so no hint asks for a disabled tool.
  const paintUnavailableReason = computed(() => {
    if (!currentImageID.value) return 'Load an image to paint';
    const effective = activeEffectiveView();
    if (effective?.kind === 'cine') return 'A clip cannot be painted';
    if (effective?.viewInfo.type === 'EnFace')
      return 'Painting is not available on an En face projection';
    if (effective?.kind === 'oblique')
      return 'Painting is not available in an oblique view';
    return '';
  });

  function activateTemporaryCrosshairs() {
    if (toolBeforeTemporaryCrosshairs.value !== undefined) return;
    if (getToolUnavailableReason(Tools.Crosshairs, activeEffectiveView()))
      return;
    toolBeforeTemporaryCrosshairs.value = currentTool.value;
    setCurrentTool(Tools.Crosshairs);
    useCrosshairsToolStore().setDragging(true);
  }

  function deactivateTemporaryCrosshairs() {
    const previous = toolBeforeTemporaryCrosshairs.value;
    if (previous === undefined) return;
    toolBeforeTemporaryCrosshairs.value = undefined;
    useCrosshairsToolStore().setDragging(false);
    applyToolSelection(coerceForEffective(previous, activeEffectiveView()));
  }

  watch(
    () => coerceForEffective(currentTool.value, activeEffectiveView()),
    (coerced) => {
      if (coerced === currentTool.value) return;
      teardownTool(currentTool.value);
      currentTool.value = coerced;
    },
    { immediate: true }
  );

  function serialize(state: StateFile) {
    const { tools } = state.manifest;
    if (!tools) return;

    Object.values(ToolStoreMap)
      .map((useStore) => useStore?.())
      .filter((store): store is IToolStore => !!store)
      .forEach((store) => {
        store.serialize?.(state);
      });

    tools.current = currentTool.value;
  }

  function deserialize(
    manifest: Manifest,
    segmentIdMap: Record<string, string>,
    dataIDMap: Record<string, string>
  ) {
    const skipped = Object.values(ToolStoreMap)
      .map((useStore) => useStore?.())
      .filter((store): store is IToolStore => !!store)
      .flatMap((store) => {
        const result = store.deserialize?.(manifest, dataIDMap, segmentIdMap);
        return Array.isArray(result) ? result : [];
      });

    if (manifest.tools?.current) {
      currentTool.value = coerceForEffective(
        manifest.tools.current,
        activeEffectiveView()
      );
    }
    return skipped;
  }

  return {
    currentTool,
    paintUnavailableReason,
    setCurrentTool,
    serialize,
    deserialize,
    activateTemporaryCrosshairs,
    deactivateTemporaryCrosshairs,
  };
});
